import { useState } from 'react';
import type { CariTransaction } from '../../../lib/cariDB';

type Props = {
  clinicName: string;
  prefilledType?: 'borc' | 'odeme';
  onSave: (data: Omit<CariTransaction, 'id' | 'created_at'>) => void;
  onClose: () => void;
};

export default function CariAddModal({ clinicName, prefilledType = 'odeme', onSave, onClose }: Props) {
  const [form, setForm] = useState({
    clinic_name: clinicName,
    type: prefilledType as 'borc' | 'odeme',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
  });

  const isValid = form.clinic_name.trim() !== '' && Number(form.amount) > 0 && form.date !== '';

  const handleSubmit = () => {
    if (!isValid) return;
    onSave({
      clinic_name: form.clinic_name.trim(),
      type: form.type,
      amount: Number(form.amount),
      date: form.date,
      description: form.description.trim(),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              {form.type === 'borc' ? 'Borç Ekle' : 'Ödeme Ekle'}
            </h3>
            <p className="text-sm text-gray-500">{clinicName || 'Yeni klinik'}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Type Toggle */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">İşlem Türü</label>
            <div className="flex gap-2 bg-gray-100 p-1 rounded-xl">
              {(['borc', 'odeme'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setForm({ ...form, type: t })}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer flex items-center justify-center gap-2 ${
                    form.type === t
                      ? t === 'borc'
                        ? 'bg-red-500 text-white'
                        : 'bg-emerald-500 text-white'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className={t === 'borc' ? 'ri-arrow-up-circle-line' : 'ri-arrow-down-circle-line'}></i>
                  {t === 'borc' ? 'Borç' : 'Ödeme'}
                </button>
              ))}
            </div>
          </div>

          {/* Clinic Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Klinik Adı</label>
            <input
              type="text"
              value={form.clinic_name}
              onChange={(e) => setForm({ ...form, clinic_name: e.target.value })}
              placeholder="Klinik adı..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent"
            />
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Tutar</label>
            <input
              type="number"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="0"
              min="0"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent font-mono"
            />
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Tarih</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Açıklama <span className="font-normal text-gray-400">(opsiyonel)</span></label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Ödeme açıklaması..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 whitespace-nowrap cursor-pointer text-sm">
              İptal
            </button>
            <button
              onClick={handleSubmit}
              disabled={!isValid}
              className={`flex-1 py-2.5 rounded-xl font-semibold text-sm whitespace-nowrap transition-all ${
                isValid
                  ? form.type === 'borc'
                    ? 'bg-red-500 text-white hover:bg-red-600 cursor-pointer'
                    : 'bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              {form.type === 'borc' ? 'Borç Ekle' : 'Ödeme Kaydet'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
